import { REPLAY_DELAY_WORDING } from 'components/admin/ReplayDelayNote/ReplayDelayNote';
import { GROUPING_BASIS, formatGroupHeading } from 'utils/groupLabel';

// "Combine ages" on a row of a meet's program (docs/school-carnivals-step-8-combined-groups.md).
//
// In a meet, a row with the tick gives its events one set of places for the
// whole group, boys and girls apart. Anything else keeps separate places for
// each age or year. The rule is said per meet, in its program; the server keeps
// it on the meet (CombinedGroups) and places results by it.
//
// The words follow the club's grouping (Doug, 2026-10-11 and 2026-10-14): a club
// that groups by year reads "Combine years" and "each year".

const isYearLevel = (basis) => basis === GROUPING_BASIS.YEAR_LEVEL;

// The words on the tick.
export const combineTickLabel = (basis) =>
  isYearLevel(basis) ? 'Combine years' : 'Combine ages';

// The numbers of a stored range ("U14-U17", "U8-U10,U12"), lowest first, each
// once; null when it is not a range of numbers ("Senior").
export const rangeNumbers = (storedRange) => {
  if (typeof storedRange !== 'string' || !storedRange.trim()) return null;
  const numbers = new Set();
  for (const segment of storedRange.split(',').map((part) => part.trim())) {
    const match = /^[Uu]?(\d{1,2})(?:-[Uu]?(\d{1,2}))?$/.exec(segment);
    if (!match) return null;
    const from = Number(match[1]);
    const to = match[2] === undefined ? from : Number(match[2]);
    if (to < from) return null;
    for (let number = from; number <= to; number += 1) numbers.add(number);
  }
  return [...numbers].sort((a, b) => a - b);
};

// Whether a program row can be combined: a row made from an age or year group
// whose range covers more than one number. The tick is shown on no other row;
// the server applies the same rule (CombinedGroups.CanCombine).
export const canCombineRow = (row) => {
  if (!row || row.groupSource !== 'AgeGroup') return false;
  const numbers = rangeNumbers(row.ageRange);
  return Boolean(numbers && numbers.length > 1);
};

// ---- The message shown when the tick is ticked or unticked ----

const joinWithAnd = (items) =>
  items.length <= 1
    ? items.join('')
    : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;

// One group number as the message names it: "U14", "14", "Year 11".
const numberName = (basis, number) =>
  isYearLevel(basis)
    ? formatGroupHeading(basis, number).replace(/^Y/, 'Year ')
    : formatGroupHeading(basis, number);

// Who the row is for, in a sentence: "boys", "girls", or "athletes" for a
// Mixed row.
const whoOf = (gender) => {
  const g = String(gender ?? '').toUpperCase();
  if (g === 'M' || g === 'BOYS') return 'boys';
  if (g === 'F' || g === 'GIRLS') return 'girls';
  return 'athletes';
};

// The row as the title names it: "U14-U17 Boys", "Junior Girls", "Junior".
const rowTitle = (groupName, gender) => {
  const who = whoOf(gender);
  return who === 'athletes'
    ? groupName
    : `${groupName} ${who.charAt(0).toUpperCase()}${who.slice(1)}`;
};

// The athletes of the group, in a sentence: "U14, U15, U16 and U17 boys",
// "14, 15, 16 and 17 year old boys", "Year 11 and Year 12 boys". A long range
// is written from its first number to its last.
const groupAthletes = (basis, numbers, who) => {
  const names = numbers.map((number) => numberName(basis, number));
  const list =
    names.length <= 5
      ? joinWithAnd(names)
      : `${names[0]} to ${names[names.length - 1]}`;
  return basis === GROUPING_BASIS.AGE ? `${list} year old ${who}` : `${list} ${who}`;
};

// "two U14s and one U15", "two 14 year olds and one 15 year old",
// "two Year 11s and one Year 12".
const exampleAthletes = (basis, first, second) =>
  basis === GROUPING_BASIS.AGE
    ? `two ${first} year olds and one ${second} year old`
    : `two ${numberName(basis, first)}s and one ${numberName(basis, second)}`;

// Where a number's own places are, in a sentence: "in U14", "among the
// 14 year olds", "in Year 11".
const amongOwn = (basis, number) =>
  basis === GROUPING_BASIS.AGE
    ? `among the ${number} year olds`
    : `in ${numberName(basis, number)}`;

const replayLine = `Season points update in the background, which can take ${REPLAY_DELAY_WORDING}.`;

// The title, paragraphs and confirm button of the message for one row.
//   basis       the club's grouping (utils/groupLabel.js)
//   groupName   the row's group ("U14-U17", "Junior")
//   ageRange    its stored range ("U14-U17")
//   gender      the row's gender ("M", "F", "Mixed")
//   eventNames  the row's events, in order ("100m", "200m")
//   combining   true when the tick is being ticked, false when unticked
export const combineAgesMessage = ({
  basis,
  groupName,
  ageRange,
  gender,
  eventNames = [],
  combining,
}) => {
  const tick = combineTickLabel(basis);
  const unit = isYearLevel(basis) ? 'year' : 'age';
  const title = rowTitle(groupName, gender);
  const who = whoOf(gender);
  const numbers = rangeNumbers(ageRange) || [];
  const events = eventNames.length ? `: ${eventNames.join(', ')}` : '';

  if (!combining) {
    return {
      title: `Stop combining ${unit}s in ${title}?`,
      paragraphs: [
        `In this meet, each ${unit} will again have its own places in this row's events${events}.`,
        `This changes who came first and the points each athlete earns. If this meet already has results, they are placed again when you save the meet. ${replayLine}`,
      ],
      confirmLabel: 'Stop combining',
    };
  }

  const paragraphs = [
    `In this meet, ${groupAthletes(basis, numbers, who)} will share one set of places in this row's events${events}. Today each ${unit} has its own places.${
      who === 'athletes' ? ' Boys and girls still have separate places.' : ''
    }`,
  ];

  if (numbers.length >= 2) {
    const [first, second] = numbers;
    // "compete in", not "run": the row's first event may be a throw or a jump.
    const event = eventNames[0] ? `the ${eventNames[0]}` : 'an event';
    paragraphs.push(
      `Example: ${exampleAthletes(basis, first, second)} compete in ${event}. Today they are placed 1st and 2nd ${amongOwn(
        basis,
        first
      )}, and 1st ${amongOwn(basis, second)}. Combined, they are placed 1st, 2nd and 3rd.`
    );
  }

  paragraphs.push(
    `This changes who came first and the points each athlete earns. Events that are not in this row keep separate places for each ${unit}.`,
    `If this meet already has results, they are placed again when you save the meet. ${replayLine}`
  );

  return { title: `${tick} in ${title}?`, paragraphs, confirmLabel: tick };
};
